import * as yup from "yup";
import Select from "react-select";
import Toast from "components/Toast";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Countries from "../../assets/countries-region.json";

const Deposit = ({ player }) => {
  const [paymentmethod, setPaymentMethod] = useState(null);
  const [showBilling, setShowBilling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(null);
  const [step] = useState(0);

  const notify = React.useCallback((type, message) => {
    Toast({ type, message });
  }, []);
  const token = document.cookie
    .split(";")
    .find((cookie) => {
      return cookie.startsWith("simba_id");
    })
    ?.split("=")[1];
  const { t } = useTranslation();

  const translateServerCode = React.useCallback(
    (code) => {
      if (!code) {
        return t("user-profile.notifications.deposit-error");
      }
      if (code === "AMOUNT_REQUIRED") {
        return t("user-profile.errors.required.amount");
      }
      if (code === "MIN_AMOUNT_10") {
        return t("user-profile.errors.min.amount");
      }
      if (code === "COIN_REQUIRED") {
        return t("user-profile.errors.required.coin");
      }
      if (code === "DEPOSIT_ERROR") {
        return t("user-profile.notifications.deposit-error");
      }

      const svError = t(`sv-errors.${code}`);
      if (svError && svError !== `sv-errors.${code}`) {
        return svError;
      }

      return t("user-profile.notifications.deposit-error");
    },
    [t],
  );

  const getErrorMessageFromResponse = React.useCallback(
    async (response) => {
      try {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const data = await response.json();
          return translateServerCode(data?.code);
        }
      } catch (error) {
        // no-op: fall back to generic translated error
      }

      return translateServerCode();
    },
    [translateServerCode],
  );

  const parseJsonOrThrow = React.useCallback(
    async (response) => {
      if (!response.ok) {
        const message = await getErrorMessageFromResponse(response);
        throw new Error(message);
      }
      return response.json();
    },
    [getErrorMessageFromResponse],
  );

  const depositSchema = yup.object().shape({
    amount: yup
      .number()
      .moreThan(0, t("user-profile.errors.required.amount"))
      .typeError(t("user-profile.errors.required.amount"))
      .required(t("user-profile.errors.required.amount")),
  });

  const billingSchema = yup.object().shape({
    address: yup
      .string()
      .test("address", t("user-profile.errors.required.address"), (value) => {
        if (showBilling) {
          return value !== "";
        }
        return true;
      }),
    country: yup
      .string()
      .test("country", t("user-profile.errors.required.country"), (value) => {
        if (showBilling) {
          return value !== "";
        }
        return true;
      }),
    zip_code: yup
      .string()
      .test("zip_code", t("user-profile.errors.required.zip-code"), (value) => {
        if (showBilling) {
          return value !== "";
        }
        return true;
      }),
    // state: yup
    //   .string()
    //   .test("state", t("user-profile.errors.required.state"), (value) => {
    //     if (showBilling) {
    //       return value !== "";
    //     }
    //     return true;
    //   }),
    city: yup
      .string()
      .test("city", t("user-profile.errors.required.city"), (value) => {
        if (showBilling) {
          return value !== "";
        }
        return true;
      }),
    first_name: yup
      .string()
      .test(
        "first_name",
        t("user-profile.errors.required.first-name"),
        (value) => {
          if (showBilling) {
            return value !== "";
          }
          return true;
        },
      ),
    last_name: yup
      .string()
      .test(
        "last_name",
        t("user-profile.errors.required.last-name"),
        (value) => {
          if (showBilling) {
            return value !== "";
          }
          return true;
        },
      ),
    coin: yup
      .string()
      .test("coin", t("user-profile.errors.required.coin"), (value) => {
        if (
          paymentmethod === "coinpayments" &&
          (!value || value.trim() === "")
        ) {
          return false;
        }
        return true;
      }),
    phone_number: yup
      .string()
      .test(
        "phone_number",
        t("user-profile.errors.required.phone-number"),
        (value) => {
          if (showBilling || paymentmethod === "paygames_mpesa") {
            return Boolean(value?.trim());
          }
          return true;
        },
      ),
  });

  const {
    register: depositRegister,
    handleSubmit: depositSubmit,
    reset: depositReset,
    trigger: depositTrigger,
    getValues: depositGetValues,
    formState: { errors: depositErrors },
  } = useForm({
    resolver: yupResolver(depositSchema),
  });

  const {
    register: billingRegister,
    handleSubmit: billingSubmit,
    reset: billingReset,
    control: billingControl,
    formState: { errors: billingErrors },
  } = useForm({
    resolver: yupResolver(billingSchema),
  });

  const handleDeposit = async (e) => {
    e.preventDefault();
    if (
      e.target.deposit_amount?.value === "" ||
      e.target.deposit_amount?.value === 0
    )
      return;
    const data = {
      amount: e.target.deposit_amount.value,
    };

    submitDeposit(data);
    e.target.deposit_amount.value = "";
  };

  async function submitDeposit(data) {
    try {
      console.log(data);
    } catch (error) {
      notify("error", t("user-profile.notifications.deposit-error"));
    }
  }

  const decideDeposit = (data) => {
    const numericAmount = Number(amount);
    const resolvedPhoneNumber = (
      data.phone_number ||
      player.phone_number ||
      ""
    ).trim();

    if (paymentmethod === "paygames_mpesa" && !resolvedPhoneNumber) {
      notify("error", t("user-profile.errors.required.phone-number"));
      return;
    }

    if (!amount || Number.isNaN(numericAmount) || numericAmount <= 0) {
      notify("error", t("user-profile.errors.required.amount"));
      return;
    }
    if (paymentmethod !== "simbalotto-gw" && numericAmount < 10) {
      notify("error", t("user-profile.errors.min.amount"));
      return;
    }

    const newData = {
      first_name: data.first_name || player.first_name,
      last_name: data.last_name || player.last_name,
      email: player.email,
      phone_number: resolvedPhoneNumber,
      address: data.address,
      country: data.country,
      zip_code: data?.zip_code,
      state: data?.state,
      city: data?.city,
      amount: numericAmount,
      coin: data?.coin,
    };

    if (paymentmethod === "broadpay") {
      sparcoCheckout(newData);
    } else if (paymentmethod === "truevo-cc") {
      truevoCheckout(newData);
    } else if (paymentmethod === "simbalotto-gw") {
      simbalottoGateway(newData);
    } else if (paymentmethod === "instaXchange") {
      instaXchange(newData);
    } else if (paymentmethod === "coinpayments") {
      coinPayments(newData);
    }
    // else if (paymentmethod === "cardiful") {
    //   setStep(1);
    //   cardifulCheckout(newData);
    // }
    else if (paymentmethod === "payop") {
      payopCheckout(newData);
    } else if (paymentmethod === "paygames_ars") {
      paygamesCheckout(newData, "ARS");
    } else if (paymentmethod === "paygames_pix") {
      paygamesCheckout(newData, "PIX");
    } else if (paymentmethod === "paygames_mpesa") {
      paygamesCheckout(newData, "KES_MPESA");
    } else if (paymentmethod === "paygames_airtel") {
      paygamesCheckout(newData, "KES_AIRTEL");
    } else if (paymentmethod === "paygames_equitel") {
      paygamesCheckout(newData, "KES_EQUITEL");
    } else if (paymentmethod === "paygames_spei") {
      paygamesCheckout(newData, "SPEI");
    } else if (paymentmethod === "finera") {
      fineraCheckout(newData);
    } else if (
      [
        "skrill",
        "skrill-neteller",
        "skrill-paysafecard",
        "skrill-rapid-bank-transfer",
      ].includes(paymentmethod)
    ) {
      const optionByPaymentMethod = {
        skrill: "skrill",
        "skrill-neteller": "neteller",
        "skrill-paysafecard": "paysafecard",
        "skrill-rapid-bank-transfer": "rapid_bank_transfer",
      };
      skrillCheckout({
        ...newData,
        payment_option: optionByPaymentMethod[paymentmethod],
      });
    }
  };

  // useEffect(() => {
  //   // Load the LencoPay script
  //   const script = document.createElement("script");
  //   script.src = "https://pay.lenco.co/js/v1/inline.js";
  //   script.async = true;
  //   script.onload = () => {
  //     console.log("LencoPay script loaded successfully");
  //   };

  //   script.onerror = () => {
  //     console.error("Failed to load the LencoPay script");
  //   };
  //   document.body.appendChild(script);

  //   return () => {
  //     // Clean up the script when the component unmounts
  //     document.body.removeChild(script);
  //   };
  // }, []);

  // useEffect(() => {
  //   const script = document.createElement("script");
  //   script.src =
  //     "https://unpkg.com/@paycore/merchant-widget-js@0.3.0/dist/merchantWidget.umd.js";
  //   script.async = true;
  //   script.onload = () => {
  //     console.log("Merchant Widget script loaded successfully");
  //   };

  //   script.onerror = () => {
  //     console.error("Failed to load the Merchant Widget script");
  //   };
  //   document.body.appendChild(script);

  //   return () => {
  //     document.body.removeChild(script);
  //   };
  // }, []);

  const sparcoCheckout = async (data) => {
    if (!data.amount) return;

    try {
      setLoading(true);
      const response = await fetch("/api/v1/lenco/deposit", {
        method: "POST",
        body: JSON.stringify({ amount: data.amount }),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const dt = await parseJsonOrThrow(response);
      await getPaidWithLenco(dt.transactionID, dt.key, data);
    } catch (error) {
      console.log(error);
      notify("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  // const cardifulCheckout = async (data) => {
  //   if (!data.amount) return;

  //   try {
  //     setLoading(true);
  //     await fetch("/api/v1/cardiful/deposit", {
  //       method: "POST",
  //       body: JSON.stringify({ amount: data.amount }),
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: "Bearer " + token,
  //       },
  //     })
  //       .then(async (response) => {
  //         if (response.ok) {
  //           return response.json();
  //         }
  //         return response.text().then((text) => {
  //           throw new Error(text);
  //         });
  //       })
  //       .then(async (dt) => {
  //         await getPaidWithCardiful(dt.transactionID, dt.key, data);
  //       })
  //       .finally(() => setLoading(false));
  //   } catch (error) {
  //     console.log(error);
  //     notify("error", error.message);
  //   }
  // };

  const payopCheckout = async (data) => {
    if (!data.amount) return;

    try {
      setLoading(true);
      const response = await fetch("/api/v1/payop/deposit", {
        method: "POST",
        body: JSON.stringify({ ...data }),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const dt = await parseJsonOrThrow(response);
      window.open(dt.payment_url, "_blank");
    } catch (error) {
      console.log(error);
      notify("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const paygamesCheckout = async (data, currency) => {
    if (!data.amount) return;

    try {
      setLoading(true);
      const response = await fetch("/api/v1/paygames/deposit", {
        method: "POST",
        body: JSON.stringify({ ...data, currency }),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      const dt = await parseJsonOrThrow(response);
      window.location.href = dt.payment_url;
    } catch (error) {
      console.log(error);
      notify("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const fineraCheckout = async (data) => {
    if (!data.amount) return;

    try {
      setLoading(true);
      const response = await fetch("/api/v1/finera/deposit", {
        method: "POST",
        body: JSON.stringify({ ...data }),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const dt = await parseJsonOrThrow(response);
      window.location.href = dt.payment_url;
    } catch (error) {
      console.log(error);
      notify("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const getPaidWithLenco = async (txnRef, key, data) => {
    if (window.LencoPay) {
      window.LencoPay.getPaid({
        key: key.toString(),
        reference: txnRef,
        email: data.email,
        amount: parseInt(data.amount),
        currency: "EUR",
        channels: ["card"],
        customer: {
          firstName: data?.first_name,
          lastName: data?.last_name,
          phone: data?.phone_number,
        },
        billing: {
          streetAddress: data?.address,
          postalCode: data?.zip_code,
          country: data?.country,
          state: data?.state,
          city: data?.city,
        },
        onSuccess: function (response) {
          (async () => {
            try {
              const statusResponse = await fetch("/api/v1/lenco/status", {
                method: "POST",
                body: JSON.stringify({ txnRef }),
                headers: {
                  "Content-Type": "application/json",
                  Authorization: "Bearer " + token,
                },
              });
              const resp = await parseJsonOrThrow(statusResponse);
              const message = translateServerCode(resp.code);
              const isSuccess = resp.code === "TV003";
              notify(isSuccess ? "success" : "error", message);
              if (isSuccess) {
                depositReset();
                billingReset();
                window.location.reload();
              }
            } catch (error) {
              console.log(error);
              notify("error", error.message);
            } finally {
              setLoading(false);
            }
          })();
        },
        onClose: function () {
          notify("error", "Payment was not completed, window closed.");
        },
        onConfirmationPending: function () {
          notify(
            "error",
            "Your purchase will be completed when the payment is confirmed",
          );
        },
      });
    } else {
      console.error("LencoPay is not loaded");
    }
  };

  // const getPaidWithCardiful = async (txnRef, key, data) => {
  //   if (window.widget) {
  //     window.widget.init({
  //       public_key: key.toString(),
  //       baseUrl: "https://pay.cardiful.com/hpp/",
  //       flow: "iframe",
  //       selector: "merchant-widget-mount-point",
  //       amount: parseInt(data.amount),
  //       currency: "EUR",
  //       language: "en",
  //       description: "Account Deposit",
  //       reference_id: txnRef,
  //       customer: {
  //         reference_id: player.id,
  //         email: player.email,
  //         name: `${data?.first_name || ""} ${data?.last_name || ""}`,
  //         phone: data?.phone_number || "25470000000",
  //         // date_of_birth: "1990-01-01",
  //         address: {
  //           full_address: data?.address || "N/A",
  //           country: data?.country || "US",
  //           region:
  //             Countries?.find(
  //               (country) => country.countryShortCode === data?.country,
  //             )?.regions[0]?.name || "N/A",
  //           city: data?.city || "N/A",
  //           street: data?.address || "N/A",
  //           post_code: data?.zip_code || "00000",
  //         },
  //       },
  //       display: {
  //         hide_header: false,
  //         hide_footer: false,
  //         hide_progress_bar: false,
  //         hide_method_filter: false,
  //         hide_lifetime_counter: false,
  //       },
  //     });
  //   } else {
  //     console.error("Cardiful is not loaded");
  //   }
  // };

  const truevoCheckout = async (data) => {
    if (!data.amount) return;

    try {
      const checkoutData = {
        amount: data.amount,
        customerFirstName: data.first_name,
        customerLastName: data.last_name,
        customerEmail: data.email,
        customerPhone: data.phone_number,
        customerAddr: data.address,
        customerCity: data.city,
        customerState: data.state,
        customerPostalCode: data.zip_code,
        customerCountryCode: data.country,
      };
      setLoading(true);

      const response = await fetch("/api/v1/truevo/deposit", {
        method: "POST",
        body: JSON.stringify(checkoutData),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        const message = await getErrorMessageFromResponse(response);
        throw new Error(message);
      }

      const htmlContent = await response.text();
      submitFormToNewPage(htmlContent);
    } catch (error) {
      console.log(error);
      notify("error", error.message);
    }
  };

  const simbalottoGateway = async (data) => {
    if (!data.amount) return;

    try {
      setLoading(true);

      const response = await fetch("/api/v1/simbalotto-gw/deposit", {
        method: "POST",
        body: JSON.stringify({ amount: data.amount }),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      const resp = await parseJsonOrThrow(response);
      if (resp && resp.redirect_url) {
        window.location.href = resp.redirect_url;
      }
    } catch (error) {
      console.log(error);
      notify("error", error.message);
    }
  };

  const coinPayments = async (data) => {
    if (!data.amount) return;

    try {
      setLoading(true);

      const response = await fetch("/api/v1/coinpayments/deposit", {
        method: "POST",
        body: JSON.stringify({ amount: data.amount, coin: data.coin }),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      const resp = await parseJsonOrThrow(response);
      if (resp && resp.redirect_url) {
        window.location.href = resp.redirect_url;
      }
    } catch (error) {
      console.log(error);
      notify("error", error.message);
      setLoading(false);
    }
  };

  const instaXchange = async (data) => {
    if (!data.amount) return;

    try {
      setLoading(true);

      const response = await fetch("/api/v1/instaxchange/deposit", {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      const resp = await parseJsonOrThrow(response);
      if (resp && resp.id) {
        const iframeLink = `https://instaxchange.com/embed/${resp.id}`;

        // Create a container for the overlay
        const overlay = document.createElement("div");
        overlay.style.position = "fixed";
        overlay.style.top = "0";
        overlay.style.left = "0";
        overlay.style.width = "100%";
        overlay.style.height = "100%";
        overlay.style.backgroundColor = "rgba(0, 0, 0, 0.7)"; // Dark semi-transparent background
        overlay.style.zIndex = "999"; // Make sure the overlay is behind the iframe but above the rest of the content

        // Create a container to hold both the iframe and the close button
        const iframeContainer = document.createElement("div");
        iframeContainer.style.position = "fixed";
        iframeContainer.style.top = "10%";
        iframeContainer.style.left = "30%";
        iframeContainer.style.zIndex = "1000"; // Above the overlay
        iframeContainer.style.width = "50%";
        iframeContainer.style.height = "70%";

        // Create iframe element
        const iframe = document.createElement("iframe");
        iframe.src = iframeLink;
        iframe.style.width = "100%"; // Set width to 100% of the container
        iframe.style.height = "100%"; // Set height to 100% of the container
        iframe.style.border = "none";

        // Create close button inside the iframe container
        const closeButton = document.createElement("button");
        closeButton.innerText = "Close";
        closeButton.style.position = "absolute";
        closeButton.style.top = "10px";
        closeButton.style.right = "10px";
        closeButton.style.zIndex = "1001"; // Ensure it's above the iframe

        // Styling the button for a more appealing look
        closeButton.style.padding = "10px 20px";
        closeButton.style.fontSize = "16px";
        closeButton.style.color = "#fff"; // White text
        closeButton.style.backgroundColor = "#ff5733"; // Orange background
        closeButton.style.border = "none";
        closeButton.style.borderRadius = "5px"; // Rounded corners
        closeButton.style.cursor = "pointer";
        closeButton.style.transition = "all 0.3s ease"; // Smooth transition for hover effects

        // Hover effect
        closeButton.addEventListener("mouseover", () => {
          closeButton.style.backgroundColor = "#ff4511"; // Darker orange on hover
        });

        closeButton.addEventListener("mouseout", () => {
          closeButton.style.backgroundColor = "#ff5733"; // Original orange when not hovering
        });

        // Event listener to remove the iframe, button, and overlay on click
        closeButton.addEventListener("click", () => {
          document.body.removeChild(iframeContainer); // Remove the iframe container (iframe and button together)
          document.body.removeChild(overlay); // Remove the overlay
          setLoading(false);
        });

        // Append the iframe and the close button to the container
        iframeContainer.appendChild(iframe);
        iframeContainer.appendChild(closeButton);

        // Append the overlay and the iframe container to the body
        document.body.appendChild(overlay);
        document.body.appendChild(iframeContainer);

        // Close the deposit modal
        document.getElementById("depositModalClose").click();
      }
    } catch (error) {
      console.log(error);
      notify("error", error.message);
      setLoading(false);
    }
  };

  async function submitFormToNewPage(htmlContent) {
    // Use DOMParser to parse the HTML content
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");

    // Extract form action URL
    const formAction = doc.querySelector("form").action;

    // Extract all input elements
    const inputs = doc.querySelectorAll("input");

    // Create a new form element
    const form = document.createElement("form");
    form.method = "POST";
    form.action = formAction;
    // form.target = "_blank"; // Ensure it opens in a new tab

    // Append input elements to the form
    inputs.forEach((input) => {
      const inputElement = document.createElement("input");
      inputElement.type = input.type;
      inputElement.name = input.name;
      inputElement.value = input.value;
      form.appendChild(inputElement);
    });

    // Append the form to the document body
    document.body.appendChild(form);

    // Submit the form
    form.submit();

    // Remove the form from the body after submission
    setTimeout(() => {
      document.body.removeChild(form);
    }, 100); // Delay to ensure form submission
  }

  const skrillCheckout = async (data) => {
    if (!data.amount) return;

    try {
      setLoading(true);
      const response = await fetch("/api/v1/skrill/deposit", {
        method: "POST",
        body: JSON.stringify({ ...data }),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const payload = await parseJsonOrThrow(response);
      const redirectOptionMap = {
        skrill: "WLT",
        neteller: "NTL",
        paysafecard: "PSC",
        rapid_bank_transfer: "OBT",
      };
      const selectedOption =
        payload?.paymentOption || data?.payment_option || "skrill";
      const redirectUrl = new URL("https://pay.skrill.com");
      redirectUrl.searchParams.set("sid", payload.sid);
      redirectUrl.searchParams.set(
        "payment_methods",
        payload?.paymentMethodCode ||
          redirectOptionMap[selectedOption] ||
          "WLT",
      );
      window.location.href = redirectUrl.toString();
    } catch (error) {
      console.log(error);
      notify("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const countries = Countries.map((country) => {
    return {
      value: country.countryShortCode,
      label: country.countryName,
      regions: country.regions.map((region) => {
        return { value: region.name, label: region.name };
      }),
    };
  });

  const coins = [
    { value: "BTC", label: "Bitcoin" },
    { value: "ETC.BEP20", label: "Ethereum Classic (BSC Chain)" },
    { value: "USDT.TRC20", label: "Tether USD (Tron/TRC20)" },
    { value: "USDT.BEP20", label: "Tether USD (BSC Chain)" },
    { value: "USDC.BEP20", label: "USD Coin (BSC Chain)" },
    { value: "DOT.BEP20", label: "Polkadot Token (BSC Chain)" },
    { value: "DOGE.BEP20", label: "Dogecoin (BSC Chain)" },
  ];

  const enabledGateways = process.env.REACT_APP_ENABLED_GATEWAYS
    ? process.env.REACT_APP_ENABLED_GATEWAYS.split(",")
    : [];
  const normalizedPlayerCountry = String(player?.country || "")
    .trim()
    .toLowerCase();
  const isBrazilUser = ["br", "bra", "brazil", "brasil"].includes(
    normalizedPlayerCountry,
  );
  const isArgentinaUser = ["ar", "arg", "argentina"].includes(
    normalizedPlayerCountry,
  );
  const isKenyaUser = ["ke", "ken", "kenya"].includes(normalizedPlayerCountry);
  const isMexicoUser = ["mx", "mex", "mexico"].includes(
    normalizedPlayerCountry,
  );

  const enabledGatewaysArray = [
    {
      order: 1,
      showBilling: false,
      type: "credit-card",
      name: "simbalotto-gw",
      enabled: enabledGateways.includes("simbalotto-gw"),
      description: t("user-profile.deposit.simbalotto-gw-desc"),
      title: null,
    },
    {
      order: 2,
      showBilling: false,
      type: "credit-card",
      name: "finera",
      enabled: enabledGateways.includes("finera"),
      description: t("user-profile.deposit.finera-desc"),
      title: null,
    },
    {
      order: 3,
      showBilling: false,
      type: "credit-card",
      name: "payop",
      enabled: enabledGateways.includes("payop"),
      description: t("user-profile.deposit.payop-desc"),
      title: t("user-profile.deposit.payop"),
    },
    {
      order: 4,
      showBilling: false,
      type: "credit-card",
      name: "paygames_ars",
      enabled: enabledGateways.includes("paygames_ars") && isArgentinaUser,
      description: t("user-profile.deposit.paygames-ars-desc"),
      title: t("user-profile.deposit.paygames-ars"),
    },
    {
      order: 5,
      showBilling: false,
      type: "credit-card",
      name: "paygames_pix",
      enabled: enabledGateways.includes("paygames_pix") && isBrazilUser,
      description: t("user-profile.deposit.paygames-pix-desc"),
      title: t("user-profile.deposit.paygames-pix"),
    },
    {
      order: 6,
      showBilling: true,
      type: "credit-card",
      name: "paygames_mpesa",
      enabled: enabledGateways.includes("paygames_mpesa") && isKenyaUser,
      description: t("user-profile.deposit.paygames-mpesa-desc"),
      title: t("user-profile.deposit.paygames-mpesa"),
    },
    {
      order: 7,
      showBilling: true,
      type: "credit-card",
      name: "paygames_airtel",
      enabled: enabledGateways.includes("paygames_airtel") && isKenyaUser,
      description: t("user-profile.deposit.paygames-airtel-desc"),
      title: t("user-profile.deposit.paygames-airtel"),
    },
    {
      order: 8,
      showBilling: true,
      type: "credit-card",
      name: "paygames_equitel",
      enabled: enabledGateways.includes("paygames_equitel") && isKenyaUser,
      description: t("user-profile.deposit.paygames-equitel-desc"),
      title: t("user-profile.deposit.paygames-equitel"),
    },
    {
      order: 9,
      showBilling: false,
      type: "credit-card",
      name: "paygames_spei",
      enabled: enabledGateways.includes("paygames_spei") && isMexicoUser,
      description: t("user-profile.deposit.paygames-spei-desc"),
      title: t("user-profile.deposit.paygames-spei"),
    },
    {
      order: 10,
      showBilling: false,
      type: "credit-card",
      name: "skrill",
      enabled: enabledGateways.includes("skrill"),
      description: "Deposit with Skrill",
      title: "Skrill",
      logoUrl: "/payment-logos/skrill.png",
    },
    {
      order: 11,
      showBilling: false,
      type: "credit-card",
      name: "skrill-neteller",
      enabled: enabledGateways.includes("skrill"),
      description: "Deposit with Neteller",
      title: "Neteller",
      logoUrl: "/payment-logos/neteller.png",
    },
    {
      order: 12,
      showBilling: false,
      type: "credit-card",
      name: "skrill-paysafecard",
      enabled: enabledGateways.includes("skrill"),
      description: "Deposit with Paysafecard",
      title: "Paysafecard",
      logoUrl: "/payment-logos/paysafecard.png",
    },
    {
      order: 13,
      showBilling: false,
      type: "credit-card",
      name: "skrill-rapid-bank-transfer",
      enabled: enabledGateways.includes("skrill"),
      description: "Deposit with Rapid bank transfer",
      title: "Rapid bank transfer",
      logoUrl: "/payment-logos/rapid-bank-transfer.png",
    },
    {
      order: 14,
      showBilling: false,
      type: "credit-card",
      name: "truevo-cc",
      enabled: enabledGateways.includes("truevo"),
      description: t("user-profile.deposit.truevo-desc"),
      title: null,
    },
    {
      order: 15,
      showBilling: false,
      type: "credit-card",
      name: "broadpay",
      enabled: enabledGateways.includes("broadpay"),
      description: t("user-profile.deposit.broadpay-desc"),
      title: t("user-profile.deposit.broadpay"),
    },
    {
      order: 1,
      showBilling: false,
      type: "crypto",
      name: "coinpayments",
      enabled: enabledGateways.includes("coinpayments"),
      description: t("user-profile.deposit.coinpayments-desc"),
      title: t("user-profile.deposit.crypto-option", { number: 1 }),
    },
    {
      order: 2,
      showBilling: false,
      type: "crypto",
      name: "instaXchange",
      enabled: enabledGateways.includes("instaXchange"),
      description: t("user-profile.deposit.instaxchange-desc"),
      title: t("user-profile.deposit.crypto-option", { number: 2 }),
    },
  ];
  const sortedGateways = [...enabledGatewaysArray]
    .filter((g) => g.enabled)
    .sort((a, b) => {
      if (a.type === b.type) {
        return a.order - b.order;
      }
      return a.type.localeCompare(b.type);
    });

  return (
    <>
      {/* deposit input */}
      <div className="card bg-secondary shadow">
        <div className="card-header bg-simbalotto-green border-0">
          <h3 className="mb-0 text-capitalize text-dark">
            {t("user-profile.headings.deposit")}
          </h3>
        </div>
        <div className="card-body">
          <form onSubmit={depositSubmit(handleDeposit)}>
            <h6 className="heading-small text-muted mb-4">
              {t("user-profile.headings.deposit-info", {
                site_name: process.env.REACT_APP_SITE_NAME,
              })}
            </h6>
            <div className="pl-lg-4">
              <div className="row">
                <div className="col-lg-6">
                  <div className="form-group">
                    <label
                      className="form-control-label text-muted"
                      htmlFor="input-amount"
                    >
                      {t("user-profile.fields.amount")}
                    </label>
                    <input
                      className="form-control form-control-alternative"
                      id="input-amount"
                      placeholder="Amount"
                      type="number"
                      name="deposit_amount"
                      {...depositRegister("amount")}
                    />

                    {depositErrors.amount && (
                      <span className="text-danger">
                        {depositErrors.amount.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 m-auto">
                  <button
                    className="btn btn-simbalotto-orange text-dark"
                    type="submit"
                    onClick={async (e) => {
                      const result = await depositTrigger("amount");
                      result && setAmount(depositGetValues("amount"));
                      result && e.target.setAttribute("data-toggle", "modal");
                      result &&
                        e.target.setAttribute("data-target", "#depositModal");
                    }}
                  >
                    <span className="btn-inner--icon text-dark">
                      <i className="fas fa-money-bill"></i>
                    </span>
                    <span className="btn-inner--text text-dark">
                      {t("user-profile.buttons.deposit")}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* deposit modal */}
      <div
        className="modal fade"
        id="depositModal"
        tabIndex="-1"
        aria-labelledby="depositModalLabel"
        aria-hidden="true"
        data-backdrop="static"
        data-keyboard="false"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
          style={{ maxWidth: "1200px", width: "100vw" }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-capitalize"
                id="depositModalCenterTitle"
              >
                {t("user-profile.deposit.title")}
                <p className="row mx-3 my-2">
                  ({t("user-profile.deposit.disclaimer")})
                </p>
              </h5>
              <span
                aria-hidden="true"
                className="close fa-2x"
                data-dismiss="modal"
                aria-label="Close"
                role="button"
                id="depositModalClose"
              >
                &times;
              </span>
            </div>

            <div
              className="modal-body"
              style={{ maxHeight: "75vh", overflowY: "auto" }}
            >
              {step === 0 && (
                <>
                  <div className="row">
                    {sortedGateways && sortedGateways.length > 0 ? (
                      sortedGateways.map((gateway, index) => (
                        <label
                          key={index}
                          className="col-6 col-md-4 mb-2"
                          role="button"
                          htmlFor={`input-${gateway.name}`}
                          onClick={() => {
                            setPaymentMethod(gateway.name);
                            setShowBilling(gateway.showBilling);
                          }}
                        >
                          <div className="card bg-secondary shadow">
                            <div
                              className={`card-body border-0
                      ${
                        paymentmethod === gateway.name
                          ? "bg-simbalotto-green"
                          : ""
                      }
                    `}
                            >
                              <div className="row align-items-center">
                                <div
                                  className="col-12 d-flex justify-content-center"
                                  style={{ minHeight: "60px" }}
                                >
                                  {gateway.logoUrl ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: "60px",
                                        width: "100%",
                                      }}
                                    >
                                      <img
                                        src={gateway.logoUrl}
                                        alt={gateway.title || gateway.name}
                                        style={{
                                          height: "50px",
                                          maxWidth: "90%",
                                          objectFit: "contain",
                                        }}
                                      />
                                    </div>
                                  ) : gateway.type === "credit-card" ? (
                                    <i className="fas fa-credit-card fa-3x text-primary" />
                                  ) : (
                                    <i className="fas fa-coins fa-3x text-primary" />
                                  )}
                                </div>
                                <div className="col-12">
                                  <h3 className="mb-0 text-center">
                                    {gateway.title
                                      ? gateway.title
                                      : t(
                                          "user-profile.deposit.credit-card-option",
                                          { number: index + 1 },
                                        )}
                                  </h3>
                                  <p>{gateway.description}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <input
                            type="radio"
                            name="payment_method"
                            value={gateway.name}
                            id={`input-${gateway.name}`}
                            hidden
                          />
                        </label>
                      ))
                    ) : (
                      <p>{t("user-profile.deposit.no-gateways")}</p>
                    )}
                  </div>

                  <form className="row" onSubmit={billingSubmit(decideDeposit)}>
                    {showBilling && (
                      <>
                        {/* input for first-name */}
                        <div className="col-12">
                          <label
                            className="form-control-label text-dark"
                            htmlFor="input-first-name"
                          >
                            {t("user-profile.fields.first-name")}
                          </label>
                          <input
                            className="form-control form-control-alternative"
                            id="input-first-name"
                            placeholder={t("user-profile.fields.first-name")}
                            type="text"
                            defaultValue={player.first_name || ""}
                            {...billingRegister("first_name")}
                          />

                          <span className="text-danger">
                            {billingErrors?.first_name?.message}
                          </span>
                        </div>

                        {/* input for last-name */}
                        <div className="col-12">
                          <label
                            className="form-control-label text-dark"
                            htmlFor="input-last-name"
                          >
                            {t("user-profile.fields.last-name")}
                          </label>
                          <input
                            className="form-control form-control-alternative"
                            id="input-last-name"
                            placeholder={t("user-profile.fields.last-name")}
                            type="text"
                            defaultValue={player.last_name || ""}
                            {...billingRegister("last_name")}
                          />

                          <span className="text-danger">
                            {billingErrors?.last_name?.message}
                          </span>
                        </div>

                        {(showBilling ||
                          paymentmethod === "paygames_mpesa") && (
                          <>
                            {/* phone number */}
                            <div className="col-12">
                              <label
                                className="form-control-label text-dark"
                                htmlFor="input-phone-number"
                              >
                                {t("user-profile.fields.phone-number")}
                              </label>
                              <input
                                className="form-control form-control-alternative"
                                id="input-phone-number"
                                placeholder={t(
                                  "user-profile.fields.phone-number",
                                )}
                                type="text"
                                defaultValue={player.phone_number || ""}
                                {...billingRegister("phone_number")}
                              />

                              <span className="text-danger">
                                {billingErrors?.phone_number?.message}
                              </span>
                            </div>
                          </>
                        )}

                        {/* input for address */}
                        <div className="col-12">
                          <label
                            className="form-control-label text-dark"
                            htmlFor="input-address"
                          >
                            {t("user-profile.fields.address")}
                          </label>
                          <input
                            className="form-control form-control-alternative"
                            id="input-address"
                            placeholder={t("user-profile.fields.address")}
                            type="text"
                            defaultValue={player.address || ""}
                            {...billingRegister("address")}
                          />

                          <span className="text-danger">
                            {billingErrors?.address?.message}
                          </span>
                        </div>

                        {/* input for country */}
                        <div className="col-12">
                          <label
                            className="form-control-label text-dark"
                            htmlFor="input-country"
                          >
                            {t("user-profile.fields.country")}
                          </label>

                          <Controller
                            name="country"
                            control={billingControl}
                            render={({ field: { onChange, value, ref } }) => (
                              <Select
                                inputRef={ref}
                                value={countries.filter(
                                  (c) => c.value === value,
                                )}
                                onChange={(val) => onChange(val.value)}
                                options={countries}
                                placeholder={t("user-profile.fields.country")}
                              />
                            )}
                          />

                          <span className="text-danger">
                            {billingErrors?.country?.message}
                          </span>
                        </div>

                        {/* input for zip code */}
                        <div className="col-12">
                          <label
                            className="form-control-label text-dark"
                            htmlFor="input-zip-code"
                          >
                            {t("user-profile.fields.postal-code")}
                          </label>
                          <input
                            className="form-control form-control-alternative"
                            id="input-zip-code"
                            placeholder={t("user-profile.fields.postal-code")}
                            type="text"
                            defaultValue={player.postal_code || ""}
                            {...billingRegister("zip_code")}
                          />

                          <span className="text-danger">
                            {billingErrors?.zip_code?.message}
                          </span>
                        </div>

                        {/* input for state */}
                        {/* <div className="col-12">
                      <label
                        className="form-control-label text-dark"
                        htmlFor="input-state"
                      >
                        {t("user-profile.fields.state")}
                      </label>
                      <input
                        className="form-control form-control-alternative"
                        id="input-state"
                        placeholder={t("user-profile.fields.state")}
                        type="text"
                        defaultValue={player.region || ""}
                        {...billingRegister("state")}
                      />

                      <span className="text-danger">
                        {billingErrors?.state?.message}
                      </span>
                    </div> */}

                        {/* input for city */}
                        <div className="col-12">
                          <label
                            className="form-control-label text-dark"
                            htmlFor="input-city"
                          >
                            {t("user-profile.fields.city")}
                          </label>
                          <input
                            className="form-control form-control-alternative"
                            id="input-city"
                            placeholder={t("user-profile.fields.city")}
                            type="text"
                            defaultValue={player.city || ""}
                            {...billingRegister("city")}
                          />

                          <span className="text-danger">
                            {billingErrors?.city?.message}
                          </span>
                        </div>
                      </>
                    )}

                    {/* input for coin */}
                    {paymentmethod === "coinpayments" && (
                      <div className="col-12">
                        <label
                          className="form-control-label text-dark"
                          htmlFor="input-coin"
                        >
                          {t("user-profile.fields.coin")}
                        </label>

                        <Controller
                          name="coin"
                          control={billingControl}
                          render={({ field: { onChange, value, ref } }) => (
                            <Select
                              inputRef={ref}
                              value={coins.filter((c) => c.value === value)}
                              onChange={(val) => onChange(val.value)}
                              options={coins}
                              placeholder={t("user-profile.fields.coin")}
                            />
                          )}
                        />

                        <span className="text-danger">
                          {billingErrors?.coin?.message}
                        </span>
                      </div>
                    )}

                    <div className="col-12 mt-4">
                      <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={loading}
                      >
                        {t("user-profile.proceed")}
                        {loading && (
                          <span
                            className="spinner-border spinner-border-sm ml-2"
                            role="status"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}

              {/* {paymentmethod && paymentmethod === "cardiful" && step === 1 && (
                <div
                  id="merchant-widget-mount-point"
                  style={{ width: "100%", height: "600px" }}
                ></div>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Deposit;
